import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyButton } from 'components/CopyButton/CopyButton';
import { DragModal, DragModalHeader } from 'components/DragModal/DragModal';
import { MM_DD_YY_HHMM } from 'utility/constants';
import { formatDate } from 'utility/functions';
import { LogViewerLabels, LogViewerRow } from './LogViewer';
import { LogStatusChipRow } from './LogViewerColumns';

/**
 * Log entry details modal
 * Opens when a log row is clicked and shows the full, untruncated message
 * plus the entry metadata (time / status / driver / level) etc.
 */
type LogDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  row: LogViewerRow | null;
  showDriver?: boolean;
  labels: LogViewerLabels;
  siteTimeZone?: string;
};

/** Small, subtle copy icon button used for the message and each field value. */
const copyIconSx = {
  p: 0.25,
  color: 'text.disabled',
  borderRadius: 1,
  '& svg': { fontSize: 16 },
  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
} as const;

type MetaRowProps = {
  label: string;
  children: ReactNode;
};
/** One labeled metadata line. */
const MetaRow = memo(({ label, children }: MetaRowProps) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ alignItems: 'center' }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 100 }}
    >
      {label}
    </Typography>
    {children}
  </Stack>
));
MetaRow.displayName = 'MetaRow';

export const LogDetailsModal = memo(
  ({
    open,
    onClose,
    row,
    showDriver = true,
    labels,
    siteTimeZone,
  }: LogDetailsModalProps) => {
    const { t } = useTranslation();
    const fields = row?.item?.fields;
    const fieldEntries = fields ? Object.entries(fields) : [];
    const level = row?.item?.level;
    const hasMessage = Boolean(row?.message);

    return (
      <DragModal
        open={open}
        onClose={onClose}
        initialWidth={640}
        initialHeight={640}
        minWidth={380}
        minHeight={360}
        isDraggable={true}
        isResizable={true}
        data-testid="log-details-modal"
        header={<DragModalHeader text={t('blox.logs.details.title')} />}
        body={
          <Box
            data-testid="log-details-modal-body"
            sx={{
              p: { xs: 1.5, sm: 2 },
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              minHeight: 0,
            }}
          >
            {/* Metadata. */}
            <Stack
              spacing={0.75}
              sx={{ flexShrink: 0 }}
            >
              <MetaRow label={t('blox.logs.details.time')}>
                <Typography variant="body2">
                  {formatDate({
                    date: row?.time ?? undefined,
                    format: MM_DD_YY_HHMM,
                    shiftTz: siteTimeZone,
                  })}
                </Typography>
              </MetaRow>

              <MetaRow label={t('blox.logs.details.status')}>
                {row ? (
                  <LogStatusChipRow
                    status={row.status}
                    labels={labels}
                  />
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </MetaRow>

              {showDriver && (
                <MetaRow label={t('blox.logs.details.driver')}>
                  <Typography variant="body2">{row?.driver}</Typography>
                </MetaRow>
              )}

              {level && (
                <MetaRow label={t('blox.logs.details.level')}>
                  <Typography variant="body2">{level}</Typography>
                </MetaRow>
              )}
            </Stack>

            <Divider sx={{ flexShrink: 0 }} />

            {/* Full message: the primary content, grows to fill the modal. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                flex: 1,
                minHeight: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {t('blox.logs.details.message')}
                </Typography>
                {hasMessage && (
                  <CopyButton
                    text={row?.message ?? ''}
                    variant="icon"
                    size="small"
                    color="inherit"
                    tooltip={t('blox.logs.details.copy_message')}
                    sx={copyIconSx}
                    data-testid="log-details-message-copy"
                  />
                )}
              </Stack>
              <Typography
                data-testid="log-details-message"
                sx={{
                  minHeight: 0,
                  overflow: 'auto',
                  pt: 0.5,
                  pr: 1,
                  fontSize: 16,
                  color: hasMessage ? 'text.primary' : 'text.secondary',
                }}
              >
                {row?.message || t('blox.logs.details.no_message')}
              </Typography>
            </Box>

            {/* Raw fields */}
            {fieldEntries.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {t('blox.logs.details.fields')}
                </Typography>
                <Box
                  data-testid="log-details-fields"
                  sx={{
                    overflow: 'auto',
                    maxHeight: 180,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  {fieldEntries.map(([key, value]) => (
                    <Stack
                      key={key}
                      direction="row"
                      spacing={1}
                      sx={{
                        px: 1,
                        py: 0.5,
                        alignItems: 'center',
                        '&:not(:last-of-type)': {
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 140, wordBreak: 'break-word' }}
                      >
                        {key}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, wordBreak: 'break-word', userSelect: 'text' }}
                      >
                        {`${value}`}
                      </Typography>
                      <CopyButton
                        text={`${value}`}
                        variant="icon"
                        size="small"
                        color="inherit"
                        tooltip={t('blox.logs.details.copy_value')}
                        sx={copyIconSx}
                        data-testid={`log-details-field-copy-${key}`}
                      />
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        }
        footer={
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ width: '100%' }}
          >
            <Button
              data-testid="log-details-close-button"
              variant="outlined"
              color="primary"
              size="medium"
              onClick={onClose}
            >
              {t('blox.actions.close')}
            </Button>
          </Stack>
        }
      />
    );
  },
);
LogDetailsModal.displayName = 'LogDetailsModal';
